// src/pages/BookFreeCounselling.jsx  (or wherever it is)
import React, { useState } from 'react';
import { saveLead } from '../services/leadService';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Calendar, Clock, Globe, 
  CheckCircle2, ArrowRight, Loader2, Sparkles, 
  GraduationCap, Briefcase
} from 'lucide-react';
const BookFreeCounselling = () => {

  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', preferredDate: '', 
    preferredTime: '', interestedCountry: '', type: 'free_counselling_study_abroad',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [focused, setFocused] = useState({});

  const handleFocus = (e) => setFocused({ ...focused, [e.target.name]: true });
  const handleBlur = (e) => {
    if (!e.target.value) setFocused({ ...focused, [e.target.name]: false });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await saveLead({
        ...formData,
        source: 'book_free_counselling',
        timestamp: new Date().toISOString(),
      });
      setIsSuccess(true);
      setFormData({
        name: '', phone: '', email: '', preferredDate: '', 
        preferredTime: '', interestedCountry: '', type: 'free_counselling_study_abroad'
      });
    } catch (error) {
      alert('Error booking slot. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // ← THIS IS THE FIX: pt-16 on mobile, pt-24 on desktop (adjust if your navbar is taller)
    <div className="min-h-screen bg-white font-sans flex flex-col lg:flex-row pt-16 lg:pt-24">
      
      
      {/* LEFT PANEL: VALUE PROPOSITION */}
      <div className="w-full lg:w-[40%] bg-slate-950 relative lg:h-screen lg:sticky lg:top-0 text-white overflow-hidden flex flex-col justify-center p-8 lg:p-12">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] right-[-20%] w-[600px] h-[600px] bg-red-900/20 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-[-20%] left-[-20%] w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-[80px]"></div>
        </div>

        <div className="relative z-10 max-w-lg mx-auto lg:mx-0">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs font-bold uppercase tracking-widest text-red-400 mb-6 mt-4">
              <Sparkles size={12} /> Free Consultation
            </div>
            
            <h1 className="text-3xl text-gray-100 font-serif font-bold mb-4 leading-tight">
              Design your <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-300">Global Career.</span>
            </h1>
            
            <p className="text-slate-300 text-base md:text-lg mb-10 leading-relaxed">
              Speak to a senior career architect. We analyze your profile, not just your grades.
            </p>

            <div className="space-y-6 border-l border-slate-700 pl-6">
              {[
                { title: "Profile Evaluation", desc: "Deep dive into your academic & work history.", icon: User },
                { title: "University Shortlisting", desc: "Matching you with Top Tier colleges.", icon: GraduationCap },
                { title: "Visa Strategy", desc: "Financial planning & documentation roadmap.", icon: Briefcase },
              ].map((step, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[29px] top-1 w-3 h-3 rounded-full bg-slate-800 border border-slate-500"></div>
                  <h3 className="text-base font-bold text-white">{step.title}</h3>
                  <p className="text-sm text-slate-400 mt-0.5">{step.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* RIGHT PANEL: FORM */}
      <div className="w-full lg:w-[60%] bg-white">
        <div className="max-w-xl mx-auto px-6 py-12 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-1">Reserve your slot</h2>
            <p className="text-slate-600 text-sm">Slots are limited. Book in advance to avoid waiting.</p>
          </motion.div>

          <AnimatePresence mode="wait">
            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600 mx-auto mb-4">
                  <CheckCircle2 size={32} />
                </div>
                <h3 className="text-2xl font-serif font-bold text-green-900 mb-2">Booking Confirmed!</h3>
                <p className="text-green-800 mb-6">Our team will call you shortly to finalize the timing.</p>
                <Link to="/success-stories" className="text-green-700 font-bold hover:underline flex items-center justify-center gap-2 text-sm">
                  Read Success Stories <ArrowRight size={14}/>
                </Link>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Details */}
                <div className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-6">
                    <FloatingInput label="Full Name" name="name" value={formData.name} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} focused={focused.name} required />
                    <FloatingInput label="Phone Number" name="phone" type="tel" value={formData.phone} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} focused={focused.phone} required />
                  </div>
                  <FloatingInput label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} focused={focused.email} required />
                </div>

                {/* Preferences */}
                <div className="space-y-5">

                  <div className="relative border-b border-slate-300 pt-5 focus-within:border-slate-900">
                    <label className="absolute top-0 left-0 text-[10px] font-bold text-slate-500 uppercase">Target Country</label>
                    <div className="flex items-center">
                      <Globe size={16} className="text-slate-500 mr-3 mb-1" />
                      <select name="interestedCountry" value={formData.interestedCountry} onChange={handleChange} className="w-full pb-1 bg-transparent outline-none text-base font-medium text-slate-900">
                        <option value="">Select Destination</option>
                        {['USA', 'UK', 'Canada', 'Australia', 'New Zealand', 'Germany', 'Ireland'].map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="relative border-b border-slate-300 pt-5 focus-within:border-slate-900">
                      <label className="absolute top-0 text-[10px] font-bold text-slate-500 uppercase">Preferred Date</label>
                      <div className="flex items-center">
                        <Calendar size={16} className="text-slate-500 mr-3 mb-1" />
                        <input type="date" name="preferredDate" value={formData.preferredDate} onChange={handleChange} min={new Date().toISOString().split('T')[0]} className="w-full pb-1 bg-transparent outline-none text-base font-medium text-slate-900" />
                      </div>
                    </div>

                    <div className="relative border-b border-slate-300 pt-5 focus-within:border-slate-900">
                      <label className="absolute top-0 text-[10px] font-bold text-slate-500 uppercase">Time Slot</label>
                      <div className="flex items-center">
                        <Clock size={16} className="text-slate-500 mr-3 mb-1" />
                        <select name="preferredTime" value={formData.preferredTime} onChange={handleChange} className="w-full pb-1 bg-transparent outline-none text-base font-medium text-slate-900">
                          <option value="">Select Time</option>
                          <option value="morning">Morning (10 AM - 1 PM)</option>
                          <option value="afternoon">Afternoon (2 PM - 5 PM)</option>
                          <option value="evening">Evening (5 PM - 8 PM)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative w-full overflow-hidden rounded-lg bg-slate-900 text-white py-4 font-bold text-base transition-all hover:bg-red-600 disabled:opacity-70 mt-8"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isSubmitting ? 'Booking Slot...' : 'Confirm Reservation'}
                    {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                  </span>
                </button>
                <p className="text-center text-[10px] text-slate-500">
                  By clicking confirm, you agree to receive a call from our expert counsellors.
                </p>
              </form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const FloatingInput = ({ label, name, value, onChange, onFocus, onBlur, focused, type = "text", required = false }) => (
  <div className="relative border-b border-slate-300 pt-5 transition-all focus-within:border-slate-900">
    <label className={`absolute left-0 transition-all duration-200 pointer-events-none ${
      focused || value ? 'top-0 text-[10px] font-bold text-slate-500 uppercase tracking-wide' : 'top-5 text-base text-slate-500'
    }`}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      required={required}
      className="w-full pb-1 bg-transparent outline-none text-base font-medium text-slate-900"
    />
  </div>
);

export default BookFreeCounselling;